import { Play, Square } from 'lucide-react'
import { cn } from '@/lib/utils'
import { UserAvatar } from '@/components/composites/user-avatar'
import { useAskMatrix } from '@/features/matrix'
import { useIdentity } from '@/features/permissions/AbilityProvider'
import { useTeamDay } from '@/features/office/api'
import type { Card } from '@/types/entities'
import { elapsedMs, formatClock } from './clock'
import { useActiveWork, useStartWork, useTick } from './api'
import { StopWorkPopover } from './StopWorkPopover'

/**
 * Start work / Stop work on the board card itself, so "I am on this now" is one click from where
 * the item is seen — and everyone looking at the board sees who is on it and for how long.
 *
 * Mine: a Start button, or a live clock with Stop. Anyone else timing the item appears as a
 * small avatar with a pulsing dot. Both read the same shared queries the header clock and the
 * team page use, so a board of fifty cards still makes two requests, not a hundred.
 */
export function TileWorkTimer({ card }: { card: Card }) {
  const { userId } = useIdentity()
  const active = useActiveWork()
  const team = useTeamDay()
  const start = useStartWork()
  const { ask, dialog } = useAskMatrix()

  const mine = active.data?.session && !active.data.session.endedAt && active.data.session.cardId === card.id ? active.data : null
  const others = (team.data?.people ?? []).filter((p) => p.working && p.card?.id === card.id && p.userId !== userId)
  const now = useTick(Boolean(mine) || others.length > 0)

  // Only the owner or an assignee may time an item; the server says so too.
  const mayTime = Boolean(userId) && (card.ownerId === userId || (card.assigneeIds ?? []).includes(userId))
  const timeable = card.status !== 'done' && !card.archived && !card.deletedAt
  if (!timeable || (!mayTime && others.length === 0)) return null

  return (
    <div className="mt-2 flex flex-wrap items-center gap-2" data-no-dnd>
      {dialog}
      {mayTime ? (
        mine?.session ? (
          <div className="inline-flex h-7 items-center gap-1.5 rounded-full border border-success/50 bg-success-soft/40 pr-0.5 pl-2.5 text-tiny">
            <span className="relative flex size-2" aria-hidden>
              <span className="absolute inline-flex size-full animate-ping rounded-full bg-ios-green opacity-60 motion-reduce:animate-none" />
              <span className="relative inline-flex size-2 rounded-full bg-ios-green" />
            </span>
            <span className="font-medium text-text-secondary">Working</span>
            <span className="num font-semibold tabular-nums" role="timer" aria-label="Time on this item">
              {formatClock(elapsedMs(mine.session.startedAt, mine.now, mine.fetchedAt, now))}
            </span>
            <StopWorkPopover>
              <button
                type="button"
                aria-label="Stop work"
                title="Stop work"
                className="grid size-6 place-items-center rounded-full hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                <Square className="size-3" fill="currentColor" />
              </button>
            </StopWorkPopover>
          </div>
        ) : (
          <button
            type="button"
            onClick={() => ask(card, () => start.mutate(card.id))}
            disabled={start.isPending}
            title={active.data?.session ? 'Stops the timer you have running elsewhere.' : 'Let the team see you are on this'}
            className={cn(
              'inline-flex h-8 items-center gap-1.5 rounded-full bg-primary px-3.5 text-xs font-semibold text-primary-foreground shadow-sm transition-colors hover:bg-primary/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring dur-fast',
              // Already in progress and not being timed: the most likely place for a missed Start.
              card.status === 'in_progress' && !active.data?.session && 'ring-2 ring-primary/35',
            )}
          >
            <Play className="size-3.5" fill="currentColor" /> Start work
          </button>
        )
      ) : null}

      {others.map((p) => (
        <span key={p.userId} className="inline-flex items-center gap-1 text-tiny text-text-muted" title={`${p.displayName} is working on this now`}>
          <span className="relative">
            <UserAvatar person={{ id: p.userId, displayName: p.displayName, photoUrl: p.photoUrl }} size="xs" />
            <span className={cn('absolute -right-0.5 -bottom-0.5 size-2 rounded-full border border-card bg-ios-green')} aria-hidden />
          </span>
          <span className="num tabular-nums">
            {formatClock(p.startedAt ? Math.max(0, now - Date.parse(p.startedAt)) : 0)}
          </span>
        </span>
      ))}
    </div>
  )
}
